import type {
  AdditionalFieldDelegate,
  AdditionalFieldDelegateProps,
} from "@firecms/core";
import { getAdminT } from "../i18n/getAdminT";
import { getAdminUiLocale } from "../i18n/getAdminUiLocale";
import type { Locale } from "../localization/constants";
import type { LevelProgress } from "../types/participantProgress";

function toDate(value: unknown): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  if (
    value != null &&
    typeof value === "object" &&
    "toDate" in value &&
    typeof (value as { toDate: () => Date }).toDate === "function"
  ) {
    const date = (value as { toDate: () => Date }).toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

/** completedAt − startedAt in whole seconds. */
export function levelProgressDurationSeconds(
  startedAt: unknown,
  completedAt: unknown
): number | null {
  const started = toDate(startedAt);
  const completed = toDate(completedAt);
  if (!started || !completed) {
    return null;
  }
  return Math.round((completed.getTime() - started.getTime()) / 1000);
}

function LevelProgressDurationCell({
  entity,
}: AdditionalFieldDelegateProps<LevelProgress>) {
  const seconds = levelProgressDurationSeconds(
    entity.values.startedAt,
    entity.values.completedAt
  );
  if (seconds == null) {
    return <span className="text-xs text-surface-400">—</span>;
  }

  return (
    <span className="text-xs tabular-nums whitespace-nowrap">{seconds}s</span>
  );
}

export function buildLevelProgressDurationField(
  locale: Locale = getAdminUiLocale()
): AdditionalFieldDelegate<LevelProgress> {
  const t = getAdminT(locale);
  return {
    key: "duration",
    name: t("participantProgress.durationColumn"),
    width: 100,
    dependencies: ["startedAt", "completedAt"],
    Builder: LevelProgressDurationCell,
    value: ({ entity }) => {
      const seconds = levelProgressDurationSeconds(
        entity.values.startedAt,
        entity.values.completedAt
      );
      return seconds == null ? "" : `${seconds}s`;
    },
  };
}
